import CryptoJS from 'crypto-js';
import QRCode from 'qrcode';
import { PDFDocument, PDFPage, rgb, StandardFonts } from 'pdf-lib';
import { PDFSignatureData } from '@/types/signature';

// Gerar token único
export function generateToken(): string {
  const timestamp = Date.now().toString();
  const random = Math.random().toString(36).substring(2);
  const uuid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36);
  const token = CryptoJS.SHA256(timestamp + random + uuid).toString().substring(0, 32);
  console.log('Token gerado:', token);
  return token;
}

// Gerar hash SHA256
export function generateHash(data: string): string {
  return CryptoJS.SHA256(data).toString();
}

// Gerar hash SHA256 de bytes
export function generateHashFromBytes(bytes: Uint8Array): string {
  return CryptoJS.SHA256(CryptoJS.lib.WordArray.create(bytes)).toString();
}

// Gerar QR Code
export async function generateQRCode(data: string): Promise<string> {
  try {
    return await QRCode.toDataURL(data, {
      width: 200,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    });
  } catch (error) {
    console.error('Erro ao gerar QR Code:', error);
    throw error;
  }
}

// Capturar foto via webcam
export async function capturePhoto(): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Não foi possível obter o contexto do canvas'));
      return;
    }

    navigator.mediaDevices.getUserMedia({ video: true })
      .then(stream => {
        video.srcObject = stream;
        video.play();

        video.addEventListener('canplay', () => {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          context.drawImage(video, 0, 0);
          
          const photo = canvas.toDataURL('image/jpeg', 0.8);
          stream.getTracks().forEach(track => track.stop());
          resolve(photo);
        });

        video.addEventListener('error', (error) => {
          stream.getTracks().forEach(track => track.stop());
          reject(error);
        });
      })
      .catch(reject);
  });
}

// Gerar PDF assinado
export async function generateSignedPDF(
  originalPDFUrl: string,
  signatureData: PDFSignatureData
): Promise<{ pdfBytes: Uint8Array; hash: string }> {
  try {
    console.log('Iniciando geração de PDF assinado...');
    console.log('URL do PDF original:', originalPDFUrl);
    
    // Carregar PDF original
    const response = await fetch(originalPDFUrl);
    
    if (!response.ok) {
      throw new Error(`Erro ao baixar PDF: ${response.status} ${response.statusText}`);
    }
    
    const pdfBytes = await response.arrayBuffer();
    console.log('PDF baixado com sucesso, tamanho:', pdfBytes.byteLength, 'bytes');
    
    if (pdfBytes.byteLength === 0) {
      throw new Error('PDF baixado está vazio');
    }
    
    const pdfDoc = await PDFDocument.load(pdfBytes);
    console.log('PDF carregado com sucesso, páginas:', pdfDoc.getPageCount());

    // Obter a última página
    const pages = pdfDoc.getPages();
    const lastPage = pages[pages.length - 1];
    const { width, height } = lastPage.getSize();

    // Carregar fontes
    const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

    // Adicionar informações de assinatura na última página
    const fontSize = 10;
    const lineHeight = fontSize + 2;
    let yPosition = height - 50;

    // Título da seção de assinatura
    lastPage.drawText('ASSINATURA ELETRÔNICA', {
      x: 50,
      y: yPosition,
      size: 14,
      font: helveticaBold,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight * 2;

    // Informações do assinante
    lastPage.drawText(`Nome do Assinante: ${signatureData.nome_assinante}`, {
      x: 50,
      y: yPosition,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight;

    lastPage.drawText(`Data e Hora da Assinatura: ${signatureData.data_assinatura}`, {
      x: 50,
      y: yPosition,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight;

    lastPage.drawText(`Endereço IP: ${signatureData.ip}`, {
      x: 50,
      y: yPosition,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight;

    lastPage.drawText(`Dispositivo: ${signatureData.user_agent}`, {
      x: 50,
      y: yPosition,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight * 2;

    // Texto legal
    lastPage.drawText('Documento assinado eletronicamente conforme MP 2.200-2/2001 e Lei 14.063/2020.', {
      x: 50,
      y: yPosition,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight;

    lastPage.drawText(`Hash SHA256: ${signatureData.hash_pdf}`, {
      x: 50,
      y: yPosition,
      size: fontSize,
      font: helveticaFont,
      color: rgb(0, 0, 0)
    });

    yPosition -= lineHeight * 2;

    // Adicionar assinatura desenhada (se disponível)
    if (signatureData.assinatura_base64) {
      try {
        const signatureImage = await pdfDoc.embedPng(signatureData.assinatura_base64);
        const signatureWidth = 150;
        const signatureHeight = 60;
        
        lastPage.drawImage(signatureImage, {
          x: 50,
          y: yPosition - signatureHeight,
          width: signatureWidth,
          height: signatureHeight
        });

        yPosition -= signatureHeight + lineHeight;
      } catch (error) {
        console.warn('Erro ao adicionar assinatura desenhada:', error);
      }
    }

    // Adicionar foto do assinante (se disponível)
    if (signatureData.foto_base64) {
      try {
        const photoImage = await pdfDoc.embedPng(signatureData.foto_base64);
        const photoWidth = 100;
        const photoHeight = 100;
        
        lastPage.drawImage(photoImage, {
          x: 50,
          y: yPosition - photoHeight,
          width: photoWidth,
          height: photoHeight
        });

        yPosition -= photoHeight + lineHeight;
      } catch (error) {
        console.warn('Erro ao adicionar foto do assinante:', error);
      }
    }

    // Adicionar QR Code para verificação
    try {
      const qrCodeDataURL = await generateQRCode(signatureData.verification_url);
      const qrCodeImage = await pdfDoc.embedPng(qrCodeDataURL);
      const qrCodeSize = 100;
      
      lastPage.drawImage(qrCodeImage, {
        x: width - qrCodeSize - 50,
        y: 50,
        width: qrCodeSize,
        height: qrCodeSize
      });

      lastPage.drawText('Escaneie para verificar', {
        x: width - qrCodeSize - 50,
        y: 30,
        size: 8,
        font: helveticaFont,
        color: rgb(0, 0, 0)
      });
    } catch (error) {
      console.warn('Erro ao adicionar QR Code:', error);
    }

    // Gerar PDF final
    const signedPdfBytes = await pdfDoc.save();
    const hash = generateHash(signedPdfBytes.toString());

    return {
      pdfBytes: signedPdfBytes,
      hash
    };
  } catch (error) {
    console.error('Erro ao gerar PDF assinado:', error);
    throw error;
  }
}

// Validar formato de email
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Formatar data para exibição
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Verificar se documento expirou
export function isDocumentExpired(expirationDate: string): boolean {
  const expiration = new Date(expirationDate);
  const now = new Date();
  return now > expiration;
}

// Gerar link de verificação
export function generateVerificationLink(token: string): string {
  return `${window.location.origin}/verificar/${token}`;
}

// Gerar link de assinatura
export function generateSignatureLink(token: string): string {
  return `${window.location.origin}/assinar/${token}`;
}

// Validar CPF
export function validateCPF(cpf: string): boolean {
  const cleanCPF = cpf.replace(/\D/g, '');
  
  if (cleanCPF.length !== 11) return false;
  
  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;
  
  // Validar primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;
  
  // Validar segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(10))) return false;
  
  return true;
}

// Sanitizar dados para segurança
export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '')
    .trim()
    .substring(0, 1000); // Limitar tamanho
}

// Gerar nome de arquivo seguro
export function generateSafeFileName(originalName: string): string {
  const timestamp = Date.now();
  const extension = originalName.split('.').pop() || 'pdf';
  const sanitizedName = originalName
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .substring(0, 50);
  
  return `${sanitizedName}_${timestamp}.${extension}`;
}
